/**
 * WAV File URL Hook
 * 
 * Custom hook for managing WAV file URL generation with on-demand loading,
 * error handling, and retry functionality. Unlike other ultrasound hooks,
 * this hook does not automatically fetch data but provides manual control
 * over URL generation.
 */

import { useState, useCallback } from 'react';
import { UltrasoundError } from '../services/equipment/types';
import { ultrasoundService } from '../services/equipment/ultrasound-service';

interface UseWavFileUrlReturn {
  /** Generated WAV file URL */
  url: string | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Ultrasound-specific error object for detailed error handling */
  ultrasoundError: UltrasoundError | null;
  /** Generate WAV file URL function */
  generateUrl: (site: string, wavId: string) => Promise<void>;
  /** Retry the last failed request */
  retry: () => Promise<void>;
  /** Clear URL and reset state */
  clear: () => void;
}

/**
 * Hook for managing WAV file URL generation on-demand
 * 
 * This hook provides manual control over WAV file URL generation,
 * allowing components to request URLs only when needed (e.g., when
 * user clicks a play button or download link).
 */
export const useWavFileUrl = (): UseWavFileUrlReturn => {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ultrasoundError, setUltrasoundError] = useState<UltrasoundError | null>(null);
  const [lastSite, setLastSite] = useState<string | undefined>(undefined);
  const [lastWavId, setLastWavId] = useState<string | undefined>(undefined);

  /**
   * Generate WAV file URL for the specified site and WAV ID
   */
  const generateUrl = useCallback(async (
    site: string,
    wavId: string
  ) => {
    if (!site || !wavId) {
      setError('Site and WAV ID are required');
      return;
    }

    setIsLoading(true);
    setError(null);
    setUltrasoundError(null);

    try {
      const wavFileUrl = await ultrasoundService.getWavFileUrl(site, wavId);
      
      setUrl(wavFileUrl);
      setLastSite(site);
      setLastWavId(wavId);
    } catch (err) {
      if (err instanceof UltrasoundError) {
        setUltrasoundError(err);
        setError(err.message);
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to generate WAV file URL';
        setError(errorMessage);
        setUltrasoundError(null);
      }
      setUrl(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Retry the last failed request
   */
  const retry = useCallback(async () => {
    if (!lastSite || !lastWavId) {
      setError('No previous request to retry');
      return;
    }
    await generateUrl(lastSite, lastWavId);
  }, []);

  /**
   * Clear URL and reset state
   */
  const clear = useCallback(() => {
    setUrl(null);
    setError(null);
    setUltrasoundError(null);
    setLastSite(undefined);
    setLastWavId(undefined);
  }, []);

  return {
    url,
    isLoading,
    error,
    ultrasoundError,
    generateUrl,
    retry,
    clear
  };
};